import { useEffect, useRef, useState } from "react";
import {
  PhoneCall, Loader2, MessageSquare, Sparkles, Wand2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const POLL_MAX_CONSECUTIVE_ERRORS = 5;

/** Poll a single Twilio call SID every 2s until the server marks it terminal. */
export function useCallTail(sid) {
  const [tracking, setTracking] = useState(null);
  const pollRef = useRef(null);
  const safetyRef = useRef(null);
  const errRef = useRef(0);

  const stop = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (safetyRef.current) { clearTimeout(safetyRef.current); safetyRef.current = null; }
    errRef.current = 0;
  };

  useEffect(() => {
    if (!sid) { stop(); setTracking(null); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/outbound/twilio/calls/${sid}`);
        if (cancelled) return;
        errRef.current = 0;
        setTracking(data);
        if (data.is_terminal) {
          stop();
          if (data.status === "completed") toast.success(`Call completed · ${data.duration_sec || 0}s`);
          else toast.error(`Call ended: ${data.status}`);
        }
      } catch (e) {
        errRef.current += 1;
        const s = e?.response?.status;
        if (s === 401 || s === 403) { stop(); toast.error("Session expired — reload to resume tailing."); return; }
        if (errRef.current >= POLL_MAX_CONSECUTIVE_ERRORS) { stop(); toast.error("Lost connection to call status."); }
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
    safetyRef.current = setTimeout(stop, 5 * 60 * 1000);
    return () => { cancelled = true; stop(); };
  }, [sid]);

  return tracking;
}

export function CallScoreCard({ score, rescoring, onRescore }) {
  const SENT = {
    positive: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Positive" },
    neutral:  { bg: "bg-slate-100",   text: "text-slate-700",   label: "Neutral" },
    negative: { bg: "bg-red-100",     text: "text-red-800",     label: "Negative" },
  };
  const INT = {
    hot:  { bg: "bg-rose-100",   text: "text-rose-800",   label: "Hot" },
    warm: { bg: "bg-amber-100",  text: "text-amber-800",  label: "Warm" },
    cold: { bg: "bg-sky-100",    text: "text-sky-800",    label: "Cold" },
  };
  const OBJ_LABEL = { price: "Price", timing: "Timing", not_interested: "Not interested", other: "Other" };
  const s = SENT[score.sentiment] || SENT.neutral;
  const i = INT[score.interest_level] || INT.cold;
  return (
    <div className="mt-2 rounded border border-primary/20 bg-primary/5 p-2" data-testid="ai-outbound-call-score">
      <div className="mb-1.5 flex items-center justify-between text-[10px] uppercase tracking-wide text-primary">
        <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> Call score</span>
        <button type="button" onClick={onRescore} disabled={rescoring}
          data-testid="ai-outbound-call-rescore"
          className="inline-flex items-center gap-1 rounded border border-primary/30 bg-white px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10 disabled:opacity-60">
          {rescoring ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
          Re-score
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.bg} ${s.text}`}
              data-testid="ai-outbound-call-sentiment">Sentiment · {s.label}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${i.bg} ${i.text}`}
              data-testid="ai-outbound-call-interest">Interest · {i.label}</span>
        {(score.objections || []).map((o) => (
          <span key={o} className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700"
                data-testid={`ai-outbound-call-objection-${o}`}>
            {OBJ_LABEL[o] || o}
          </span>
        ))}
      </div>
      {score.summary_line && (
        <div className="mt-2 text-xs text-slate-700 italic" data-testid="ai-outbound-call-summary">
          &ldquo;{score.summary_line}&rdquo;
        </div>
      )}
    </div>
  );
}

export function CallStatusView({ tracking }) {
  const [transcribing, setTranscribing] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [transcriptOverride, setTranscriptOverride] = useState(null);
  const [scoreOverride, setScoreOverride] = useState(null);
  useEffect(() => { setTranscriptOverride(null); setScoreOverride(null); }, [tracking?.call_sid]);

  if (!tracking) return <div className="text-xs text-slate-400 py-2">Loading call status…</div>;
  const statusColor = (s) => {
    if (s === "completed") return "text-emerald-600";
    if (["busy", "no-answer", "canceled", "failed"].includes(s)) return "text-red-600";
    return "text-blue-600";
  };
  const transcript = transcriptOverride ?? tracking.transcript;
  const transcribedAt = transcriptOverride ? new Date().toISOString() : tracking.transcribed_at;
  const score = scoreOverride ?? {
    sentiment: tracking.sentiment,
    interest_level: tracking.interest_level,
    objections: tracking.objections,
    summary_line: tracking.summary_line,
    scored_at: tracking.scored_at,
  };

  const runTranscribe = async () => {
    setTranscribing(true);
    try {
      const { data } = await api.post(`/outbound/twilio/calls/${tracking.call_sid}/transcribe`);
      setTranscriptOverride(data.transcript || "");
      setScoreOverride({
        sentiment: data.sentiment,
        interest_level: data.interest_level,
        objections: data.objections || [],
        summary_line: data.summary_line,
        scored_at: data.scored_at,
      });
      toast.success(data.cached ? "Loaded cached transcript." : "Transcribed & scored.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transcription failed");
    } finally { setTranscribing(false); }
  };

  const runRescore = async () => {
    setRescoring(true);
    try {
      const { data } = await api.post(`/outbound/twilio/calls/${tracking.call_sid}/score`);
      setScoreOverride(data);
      toast.success("Re-scored the call.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scoring failed");
    } finally { setRescoring(false); }
  };

  return (
    <div className="space-y-3 py-2" data-testid="ai-outbound-test-call-tracking">
      <div className="rounded-md border p-3 bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="text-xs font-mono text-muted-foreground truncate" data-testid="ai-outbound-test-call-sid">
            SID {tracking.call_sid}
          </div>
          <div className={`text-sm font-semibold capitalize ${statusColor(tracking.status)}`}
               data-testid="ai-outbound-test-call-status">
            {tracking.is_terminal
              ? (tracking.status || "").replace(/-/g, " ")
              : <span className="inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> {(tracking.status || "").replace(/-/g, " ")}
                </span>}
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-muted-foreground">To:</span>{" "}
            <span className="font-mono">{tracking.to || "—"}</span>
          </div>
          <div><span className="text-muted-foreground">Duration:</span>{" "}
            <span data-testid="ai-outbound-test-call-duration">{tracking.duration_sec != null ? `${tracking.duration_sec}s` : "—"}</span>
          </div>
        </div>
      </div>
      {tracking.recording_sid && (
        <div className="rounded-md border p-2 text-xs" data-testid="ai-outbound-test-call-recording">
          <div className="mb-1 flex items-center justify-between">
            <span className="font-medium text-slate-700">Call recording</span>
            {tracking.recording_duration_sec != null && (
              <span className="text-muted-foreground">{tracking.recording_duration_sec}s</span>
            )}
          </div>
          <audio controls preload="none" className="w-full h-8"
                 src={`${process.env.REACT_APP_BACKEND_URL}/api/outbound/twilio/calls/${tracking.call_sid}/recording`}
                 data-testid="ai-outbound-test-call-recording-audio" />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">
              {transcript ? "Whisper transcript ready" : "Whisper transcription (OpenAI)"}
            </span>
            <button type="button" onClick={runTranscribe} disabled={transcribing}
              data-testid="ai-outbound-test-call-transcribe"
              className="inline-flex items-center gap-1 rounded-md border border-primary/20 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/10 disabled:opacity-60">
              {transcribing ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageSquare className="h-3 w-3" />}
              {transcript ? "Re-transcribe" : "Transcribe"}
            </button>
          </div>
          {transcript && (
            <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2"
                 data-testid="ai-outbound-test-call-transcript">
              <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-slate-500">
                <span>Transcript</span>
                {transcribedAt && <span>{new Date(transcribedAt).toLocaleString()}</span>}
              </div>
              <div className="whitespace-pre-wrap text-xs text-slate-700 leading-relaxed">{transcript}</div>
            </div>
          )}
          {transcript && score.sentiment && (
            <CallScoreCard score={score} rescoring={rescoring} onRescore={runRescore} />
          )}
        </div>
      )}
      {(tracking.events || []).length > 0 && (
        <div className="rounded-md border p-2 max-h-40 overflow-y-auto text-xs space-y-1"
             data-testid="ai-outbound-test-call-events">
          {(tracking.events || []).map((ev, i) => (
            <div key={i} className="flex items-center justify-between font-mono">
              <span className="capitalize">{(ev.status || "").replace(/-/g, " ")}</span>
              <span className="text-muted-foreground">{ev.at ? new Date(ev.at).toLocaleTimeString() : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CallTailDialog({ sid, open, onClose }) {
  const tracking = useCallTail(open ? sid : null);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="ai-outbound-tail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PhoneCall className="h-4 w-4 text-primary" /> Watching call status</DialogTitle>
          <DialogDescription>Live status updates. Refreshing every 2s until the call ends.</DialogDescription>
        </DialogHeader>
        <CallStatusView tracking={tracking} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ai-outbound-tail-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
